import { useEffect, useMemo, useRef } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import type { DemoOriginsContext } from "../utils/demoRequestOriginSeed";
import { demoOriginsOf, demoOriginsSignature } from "../utils/demoRequestOrigins";
import { type RequestOrigins, RequestOriginsStatus, type ShiftRequest } from "../utils/models";
import { useDemoOriginsContext } from "../utils/useDemoOriginsContext";

export type RequestOriginsResult = { status: RequestOriginsStatus; byRequestId: ReadonlyMap<string, RequestOrigins> };

const UNAVAILABLE: RequestOriginsResult = { status: RequestOriginsStatus.Unavailable, byRequestId: new Map() };

type CachedOrigins = { signature: string; origins: RequestOrigins };

/**
 * Why each request exists, keyed by request id. Only demo departments get origins for now; every other department
 * reads `Unavailable` with an empty map, so callers render exactly what they did before. Call it once per surface and
 * pass the map down. A request whose fields did not change keeps the same `RequestOrigins` object across refreshes, so
 * memoized rows holding it do not re-render.
 */
export const useRequestOrigins = (departmentId: string, requests: readonly ShiftRequest[]): RequestOriginsResult => {
	const { trackError } = useAnalytics();
	const { isRealDemo, chosenDepartment } = useHospitalData();
	// The roster behind the seeding is the loaded department's; a caller asking about another one gets nothing.
	const isAvailable = isRealDemo && chosenDepartment.department_id === departmentId;
	const demoContext = useDemoOriginsContext(isAvailable);
	// Keyed to the seeding context: a roster or clock change re-seeds everything, a request refresh alone does not.
	const cacheRef = useRef<{ context: DemoOriginsContext | null; byRequestId: Map<string, CachedOrigins> }>({ context: null, byRequestId: new Map() });

	const result = useMemo<RequestOriginsResult>(() => {
		if (demoContext === null) return UNAVAILABLE;
		if (cacheRef.current.context !== demoContext) cacheRef.current = { context: demoContext, byRequestId: new Map() };
		const cache = cacheRef.current.byRequestId;
		const byRequestId = new Map<string, RequestOrigins>();
		for (const request of requests) {
			const signature = demoOriginsSignature(request);
			const cached = cache.get(request.id);
			const origins = cached !== undefined && cached.signature === signature ? cached.origins : demoOriginsOf(request, demoContext);
			cache.set(request.id, { signature, origins });
			byRequestId.set(request.id, origins);
		}
		return { status: RequestOriginsStatus.Ready, byRequestId };
	}, [requests, demoContext]);

	useEffect(() => {
		if (result.status === RequestOriginsStatus.Failed) trackError("request_origins_load", { department_id: departmentId });
	}, [result.status, departmentId, trackError]);

	return result;
};
